import type { FC, ReactNode } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import BarWithPill from 'src/components/followUp/common/Bar/BarWithPill';

import { styles } from './index.styles';

interface HorizontalBarChartBar {
  label: string;
  value: number;
  relativeValue?: number;
  pillLabel?: ReactNode;
  tooltip?: string;
}

interface HorizontalBarChartProps {
  bars: HorizontalBarChartBar[];
  totalObjects?: number;
  noDataAvailable?: boolean;
}

const HorizontalBarChart: FC<HorizontalBarChartProps> = ({
  bars,
  totalObjects,
  noDataAvailable,
}) => {
  const { colors } = useTheme();
  return (
    <Box paddingX={2.5} paddingBottom={2} width="100%" height="100%">
      <Stack direction="column" gap={1}>
        {bars.map((bar) => {
          const relativeValue =
            bar.relativeValue ?? getRelativeValue(bar.value, totalObjects);
          const pillLabel = bar.pillLabel ?? (
            <Stack
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="caption2"
                color={colors.secondary[800]}
                fontWeight={600}
              >
                {relativeValue.toFixed()}%
              </Typography>
              <Typography
                variant="caption2"
                color={colors.secondary[800]}
                bgcolor={colors.secondary[200]}
              >
                {bar.value.toLocaleString()}
              </Typography>
            </Stack>
          );
          return (
            <Stack
              direction="row"
              gap={1}
              width="100%"
              height="4rem"
              key={bar.label}
              pr={8} // Account for pill with label text
            >
              <Stack sx={styles.rowLabel}>
                <Typography variant={'body3'} fontWeight={600}>
                  {bar.label}
                </Typography>
              </Stack>
              <BarWithPill
                relativeSize={relativeValue}
                color={colors.secondary[500]}
                borderColor={colors.secondary[400]}
                type="horizontal"
                borderPosition="Right"
                withGradient
                toolTip={bar.tooltip}
                noDataAvailable={noDataAvailable}
                pill={{
                  label: pillLabel,
                  color: 'primary',
                  squared: true,
                }}
              />
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default HorizontalBarChart;
